/**
 * Hala CRM - Pool Requests Page
 * Shows available requests in group pool for claiming
 * Note: This page is NOT accessible to Consultants (they use direct assignment)
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Inbox, Search, RefreshCw, User, ChevronLeft, Clock, HandHeart,
  CheckCircle, FileText, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

export default function CRMPoolPage() {
  const { user, getCRMHeaders } = useOutletContext();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [search, setSearch] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  // Check if user is consultant (blocked from pool)
  const isConsultant = user?.role_id === 'consultant';

  const fetchPool = useCallback(async () => {
    if (!user || isConsultant) {
      setLoading(false);
      if (isConsultant) setAccessDenied(true);
      return;
    }
    setLoading(true);
    
    try {
      const res = await axios.get(
        `${API}/requests/pool-for-user/${user.user_id}`,
        { headers: getCRMHeaders() }
      );
      setRequests(res.data || []);
    } catch (e) {
      console.error('Failed to fetch pool:', e);
      if (e.response?.status === 403) {
        setAccessDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, [user, isConsultant, getCRMHeaders]);

  useEffect(() => {
    fetchPool();
  }, [fetchPool]);

  const handleClaim = async (requestId, e) => {
    e.stopPropagation();
    setClaiming(requestId);
    
    try {
      await axios.post(
        `${API}/requests/${requestId}/claim`,
        { user_id: user.user_id },
        { headers: getCRMHeaders() }
      );
      // Remove from list
      setRequests(prev => prev.filter(r => r.request_id !== requestId));
      // Navigate to request
      navigate(`/crm/request/${requestId}`);
    } catch (e) {
      console.error('Failed to claim:', e);
      alert(e.response?.data?.detail || 'فشل المطالبة بالطلب');
    } finally {
      setClaiming(null);
    }
  };

  // Access denied for consultants
  if (accessDenied || isConsultant) {
    return (
      <div style={{ 
        direction: 'rtl', 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: COLORS.errorLight,
        borderRadius: '16px',
        margin: '40px 0',
      }}>
        <AlertCircle size={56} color={COLORS.error} style={{ marginBottom: '20px' }} />
        <h2 style={{ color: COLORS.error, margin: '0 0 12px', fontSize: '22px' }}>
          غير مصرح بالوصول
        </h2>
        <p style={{ color: COLORS.textMuted, margin: '0 0 24px', fontSize: '15px', lineHeight: 1.7 }}>
          كمستشار، لا يمكنك الوصول لمجمع الطلبات.
          <br />
          الطلبات تُسند إليك مباشرة بناءً على تخصصك.
        </p>
        <button
          onClick={() => navigate('/crm/my-requests')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: '10px',
            backgroundColor: COLORS.primary,
            color: COLORS.white,
            fontSize: '15px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          الذهاب إلى طلباتي
        </button>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const filteredRequests = search
    ? requests.filter(r => 
        r.applicant_name?.includes(search) ||
        r.service_name_ar?.includes(search) ||
        r.mobile?.includes(search)
      )
    : requests;

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
          مجمع الطلبات
        </h1>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
          الطلبات المتاحة للمطالبة ({requests.length} طلب)
        </p>
      </div>

      {/* Info Banner */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px 20px',
        backgroundColor: COLORS.warningLight,
        borderRadius: '12px',
        marginBottom: '24px',
        border: `1px solid ${COLORS.warning}30`,
      }}>
        <HandHeart size={24} color={COLORS.warning} />
        <div>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
            اختر طلباً للمطالبة به
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
            بمجرد المطالبة، سيصبح الطلب مُسنداً لك وسيختفي من المجمع
          </p>
        </div>
      </div>

      {/* Search */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        alignItems: 'center',
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="بحث بالاسم أو الخدمة أو الجوال..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px 10px 40px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              textAlign: 'right',
            }}
          />
        </div>
        <button
          onClick={fetchPool}
          style={{
            padding: '10px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            backgroundColor: COLORS.white,
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={18} color={COLORS.textMuted} />
        </button>
      </div>

      {/* Pool Cards */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
          جاري التحميل...
        </div>
      ) : filteredRequests.length === 0 ? (
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '12px',
          padding: '60px',
          textAlign: 'center',
          border: `1px solid ${COLORS.border}`,
        }}>
          <Inbox size={48} style={{ marginBottom: '16px', opacity: 0.4 }} color={COLORS.textMuted} />
          <p style={{ margin: 0, color: COLORS.textMuted }}>
            {search ? 'لا توجد نتائج' : 'لا توجد طلبات في المجمع'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          {filteredRequests.map((req) => (
            <div
              key={req.request_id}
              style={{
                backgroundColor: COLORS.white,
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => navigate(`/crm/request/${req.request_id}`)}
            >
              <div style={{ padding: '20px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      backgroundColor: COLORS.bgDark,
                      fontSize: '12px',
                      color: COLORS.textMuted,
                      marginBottom: '8px',
                    }}>
                      {req.request_id?.substring(0, 12)}...
                    </span>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
                      {req.service_name_ar || 'خدمة غير محددة'}
                    </h3>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    backgroundColor: `${COLORS.gold}20`,
                    color: COLORS.gold,
                    fontSize: '12px',
                    fontWeight: 500,
                  }}>
                    {req.program_name_ar || '-'}
                  </span>
                </div>

                {/* Applicant */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: COLORS.bgDark,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <User size={20} color={COLORS.textMuted} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                      {req.applicant_name || 'غير محدد'}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
                      {req.mobile}
                    </p>
                  </div>
                </div>

                {/* Time */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
                  <Clock size={14} color={COLORS.textMuted} />
                  <span style={{ fontSize: '12px', color: COLORS.textMuted }}>
                    {formatDate(req.created_at)}
                  </span>
                </div>

                {/* Claim Button */}
                <button
                  onClick={(e) => handleClaim(req.request_id, e)}
                  disabled={claiming === req.request_id}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: COLORS.primary,
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: claiming === req.request_id ? 'not-allowed' : 'pointer',
                    opacity: claiming === req.request_id ? 0.7 : 1,
                    transition: 'all 0.2s',
                  }}
                >
                  {claiming === req.request_id ? (
                    'جاري المطالبة...'
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      المطالبة بالطلب
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
